import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { rxResource } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { RouterLink } from '@angular/router';
import { Api } from '../../api/api';
import { getPriceChart } from '../../api/functions';
import { LocationStateService } from '../../common/services/location-state.service';
import { DayDatepickerComponent } from '../dashboard/components/datepicker/day-datepicker.component';
import { MonthDatepickerComponent } from '../dashboard/components/datepicker/month-datepicker.component';
import { PriceLineChartComponent } from './price-line-chart.component';

type PriceInterval = 'day' | 'month';

/**
 * Strom-Preis: the location's purchase and sale prices over a day or a month, laid out like the
 * performance charts. Year and total are shown but greyed out.
 */
@Component({
  selector: 'app-price-chart',
  imports: [
    MatButtonToggleGroup,
    MatButtonToggle,
    RouterLink,
    DayDatepickerComponent,
    MonthDatepickerComponent,
    PriceLineChartComponent
],
  templateUrl: './price-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './price-chart.component.css',
})
export class PriceChartComponent {
  private readonly api = inject(Api);
  private readonly locationStateService = inject(LocationStateService);

  isLoading = signal<boolean>(false);
  selectedInterval = signal<PriceInterval>('day');
  selectedDate = signal(new Date());

  params = computed(() => ({
    location_id: this.locationStateService.currentLocation()?.id ?? '',
    aggregation: this.selectedInterval(),
    at: this.selectedDate().toISOString(),
  }));

  resource = rxResource({
    params: this.params,
    stream: ({ params }) => {
      this.isLoading.set(true);
      return from(this.api.invoke(getPriceChart, params)).pipe(finalize(() => this.isLoading.set(false)));
    },
  });

  /** No prices for the chosen period, e.g. tomorrow before the optimizer has run. */
  noPrices = computed(() => !this.isLoading() && this.resource.hasValue() && !this.resource.value()?.points?.length);

  onIntervalSelectionChange(event: MatButtonToggleChange) {
    this.selectedInterval.set(event.value);
  }

  updateDate(date: Date) {
    if (date) {
      this.selectedDate.set(date);
    }
  }
}
